import { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import { B300, B400, N0, N50, N500, N800 } from '@atlaskit/theme/colors';
import { SanitizedFormattedHtmlMessageProps } from '@atlassiansox/bxpkit-core';
import {
  SanitizedFormattedHtmlMessage,
  useStargateClient
} from '@atlassiansox/bxpkit-core';
import { catalog } from '@atlassiansox/metal-client';
import styled from '@emotion/styled';
import debounce from 'lodash.debounce';
import React, { useEffect, useMemo, useState } from 'react';
import { metalClient } from '../../../../metrics';
import {
  updateLoggedInFormDataProducts,
  updateLoggedInFormInput,
  updateLoggedInFormValid,
  useAppDispatch,
  useAppSelector
} from '../../../../redux';
import { TooltipIcon } from '../../../legacy/view/sign-up-form/Icons/tooltip-icon';
import { useDevopsState } from '../../hook';
import { messages } from '../../messages';
import { ValidationStateType } from '../../types';
import { buildDevopsProduct, getSiteDomain } from '../../utility';
import { FieldType } from '../model';
import { fontCharlieText } from '../styled';
import { ErrorMessage, FieldWrapper } from '../styled';
import { IconValidation } from './icon-validation';

export function InputSitename() {
  const { intl, userInfo } = useDevopsState();
  const dispatch = useAppDispatch();
  const [isSitenameNew, setIsSitenameNew] = useState(true);
  const [hasFocus, setHasFocus] = useState(false);
  const stargateClient = useStargateClient();
  const validSitename = /^[a-z0-9]+[a-z0-9-]+[a-z0-9]+$/;

  const [inputValue, setInputValue] = useState('');
  const sites = userInfo?.sites || [];
  const showSiteNameToggle = sites?.length > 0;
  const siteDomain = getSiteDomain();
  const [validationState, setValidationState] = useState<ValidationStateType>();
  const [validationMessage, setValidationMessage] = useState<
    undefined | SanitizedFormattedHtmlMessageProps
  >();
  const { bbWorkspaceName } = useAppSelector(
    ({ loggedInFormData }) => loggedInFormData
  );

  useEffect(() => {
    if (inputValue === '') {
      checkSitenameNewAsyncDebounced.cancel();
      setValidationMessage(undefined);
      setValidationState(undefined);
    } else {
      setHasFocus(true);
      void validateSitename(inputValue);
    }
  }, [inputValue]);

  useEffect(() => {
    if (
      userInfo !== undefined &&
      userInfo.sites !== null &&
      userInfo.sites.length === 1
    ) {
      setIsSitenameNew(false);
      setHasFocus(true);
      setInputValue(userInfo.sites[0].displayName);
    }
  }, [userInfo]);

  useEffect(() => {
    if (isSitenameNew) {
      setInputValue('');
      setValidationMessage(undefined);
      setValidationState(undefined);
      dispatch(updateLoggedInFormValid(false));
    } else {
      if (
        userInfo !== undefined &&
        userInfo.sites !== null &&
        userInfo.sites.length === 1
      ) {
        setHasFocus(true);
        setInputValue(userInfo.sites[0].displayName);
      }
    }
  }, [isSitenameNew]);

  const checkSitenameNewAsyncDebounced = useMemo(
    () =>
      debounce(
        async (value: string) => {
          let breachStatus = false;
          const existingWorkspace = !!bbWorkspaceName;
          let siteNameCheck;
          const shouldNotValidateExisting =
            value !== (userInfo?.sites || [])[0]?.displayName;
          if (shouldNotValidateExisting) {
            siteNameCheck = await stargateClient.checkCloudNameAvailability({
              cloudName: value,
              webPlatform: 'magnolia'
            });
          }

          if (!isSitenameNew) {
            const selectedSite = sites[0];
            const breachCheck = await Promise.all(
              selectedSite.products.map(async (product) => {
                const status = await stargateClient.postExpandFreeValidation({
                  cloudId: selectedSite?.cloudId,
                  productKeys: [product]
                });
                return status.isInBreachOfFree;
              })
            );
            breachStatus = breachCheck.includes(true);
          }

          if (
            siteNameCheck?.availability !== 'AVAILABLE' &&
            isSitenameNew &&
            shouldNotValidateExisting
          ) {
            dispatch(updateLoggedInFormValid(false));
            setValidationState('error');
            setValidationMessage(messages.errorSitenameUnavailable);
            metalClient.error.submit({
              name: catalog.error.INPUT,
              page: 'sign-up-layout-devops',
              component: 'devops-input-sitename'
            });
            return 'error';
          } else {
            const selectedSite = !shouldNotValidateExisting
              ? sites[0]
              : undefined;
            const { productsNew, productsExisting } = buildDevopsProduct({
              cloudName: value,
              breachStatus,
              existingWorkspace,
              selectedSite
            });
            dispatch(
              updateLoggedInFormDataProducts({
                cloudName: value,
                productsNew,
                productsExisting
              })
            );
            dispatch(updateLoggedInFormValid(true));
            setValidationState('success');
          }
          return undefined;
        },
        750,
        { trailing: true }
      ),
    []
  );

  async function validateSitename(value: string) {
    if (!hasFocus) {
      return undefined;
    }

    setValidationMessage(undefined);
    if (value.length === 0) {
      setValidationState(undefined);
      dispatch(updateLoggedInFormValid(false));
      return undefined;
    }
    if (!validSitename.test(value) || (value.length > 0 && value.length < 3)) {
      setValidationState('error');
      dispatch(updateLoggedInFormValid(false));
      setValidationMessage(messages.errorSitenameInvalid);
      return 'error';
    }
    setValidationState('validating');
    return checkSitenameNewAsyncDebounced(value);
  }

  function SiteNameToggle() {
    switch (isSitenameNew) {
      case true:
        return (
          <SanitizedFormattedHtmlMessage
            {...messages.inputToggleSitenameExisting}
          />
        );
      default:
        return (
          <SanitizedFormattedHtmlMessage {...messages.inputToggleSitenameNew} />
        );
    }
  }
  const handleInputChange = (event: { target: HTMLInputElement }) => {
    setInputValue(event?.target?.value);
  };

  const toolTipContainer = (
    <ToolTipContainer>
      <DomainToolTip data-testid="domain-tool-tip">
        <TooltipIcon fillColor={N50} />
      </DomainToolTip>
      <ToolTipText className="domain-name-tooltip-trigger">
        {intl.formatMessage(messages.errorSitenameInvalid)}
      </ToolTipText>
    </ToolTipContainer>
  );

  const labelAndToolTip = (
    <LabelContainer>
      <div>{intl.formatMessage(messages.inputLabelSitenameNew)}</div>
      {toolTipContainer}
    </LabelContainer>
  );

  return (
    <StyledField>
      <Field
        name="sitenameNew"
        defaultValue=""
        label={labelAndToolTip}
        isRequired
        isDisabled={!isSitenameNew}
      >
        {({ fieldProps }: FieldType) => {
          return (
            <FieldWrapper
              validationState={validationState}
              isDisabled={fieldProps.isDisabled}
            >
              <TextField
                {...fieldProps}
                onFocus={() => setHasFocus(true)}
                onBlur={() => setHasFocus(false)}
                data-testid="devops-input-sitename"
                elemAfterInput={
                  <SiteNameAfter>
                    <SiteNameAfterLabel>{siteDomain}</SiteNameAfterLabel>
                    <IconValidation validationState={validationState} />
                  </SiteNameAfter>
                }
                onChange={handleInputChange}
                value={inputValue}
              />

              {validationMessage && (
                <ErrorMessage data-testid="error-message">
                  <SanitizedFormattedHtmlMessage {...validationMessage} />
                </ErrorMessage>
              )}
            </FieldWrapper>
          );
        }}
      </Field>
      {showSiteNameToggle && (
        <SitenameToggleWrapper
          onClick={() => {
            // if there is more than 1 site input should switch to drop down
            if (userInfo && sites?.length > 1) {
              dispatch(updateLoggedInFormInput(false));
            } else {
              setIsSitenameNew(!isSitenameNew);
            }
          }}
        >
          <SiteNameToggle />
        </SitenameToggleWrapper>
      )}
      <SitenameDescription>
        <SanitizedFormattedHtmlMessage
          {...messages.inputDescriptionSitenameNew}
        />
      </SitenameDescription>
    </StyledField>
  );
}
const StyledField = styled.div`
  label {
    width: 100%;
  }
`;
const LabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
const SitenameToggleWrapper = styled.div`
  font-family: ${fontCharlieText};
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: ${B300};
  vertical-align: middle;
  margin: 0 0 16px 0;
  font-weight: 500;
  cursor: pointer;
`;

const SitenameDescription = styled.div`
  color: ${N500};
  font-weight: 400;
  font-size: 12px;
  margin: 3px 0px;
`;
const SiteNameAfterLabel = styled.span`
  font-size: 14px !important;
  white-space: nowrap;
  padding-right: 8px;
`;
const SiteNameAfter = styled.div`
  padding-right: 8px;
  display: flex;
  font-size: 16px;
  align-items: center;
  justify-content: flex-end;
  width: auto;
  color: #7a869a;
  font-weight: 400;
  svg {
    margin-bottom: 0px;
  }
  span {
    display: flex;
    align-items: center;
    min-width: 24px;
  }
`;
const ToolTipContainer = styled.div`
  position: relative;
  z-index: 11;
  display: none;
  @supports (display: grid) {
    display: flex;
    align-items: center;
  }
  :hover {
    .domain-name-tooltip-trigger {
      display: block;
    }
    path {
      fill: ${B400};
    }
  }
`;
const DomainToolTip = styled.div`
  cursor: pointer;
  margin: 0 6px;
  border-radius: 3px;
  svg {
    height: 14px;
    width: 14px;
  }
`;
const ToolTipText = styled.div`
  display: none;
  background: ${N0};
  border: 1px solid ${B400};
  border-radius: 2px;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
  color: ${N800};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.42857143;
  padding: 12px;
  z-index: 1;
  position: absolute;
  right: 6px;
  top: 35px;
  width: 298px;
  span {
    display: block !important;
  }
`;
